/**
 * Caisse hors ligne.
 *
 * - Le catalogue de la caisse (produits, prix, codes-barres, stock) est gardé sur
 *   l'appareil et mis à jour à chaque connexion.
 * - Sans connexion, une vente est enregistrée sur l'appareil avec un numéro
 *   provisoire (HL-…) et son ticket s'imprime.
 * - Au retour de la connexion, les ventes partent toutes seules, dans l'ordre.
 *   Chaque vente porte une clé unique (en-tête Idempotency-Key) : renvoyée deux
 *   fois, elle n'est enregistrée qu'une fois.
 * - Une vente refusée par le serveur est gardée à part, avec le motif, pour le gérant.
 * - Au-delà de 24 h sans envoi, la caisse se bloque jusqu'au retour de la connexion.
 */
import { reactive } from "vue";
import api from "@/api";
import { lire, ecrire } from "./stockage";

export const DELAI_MAX_HEURES = 24;
const INTERVALLE_MS = 30000;

export const horsLigne = reactive({
  /** Le serveur répond (mis à jour à chaque requête) */
  joignable: typeof navigator === "undefined" ? true : navigator.onLine,
  enAttente: [],
  rejetees: [],
  envoiEnCours: false,
  catalogueLe: null,
  pret: false,
});

let store = null;
let minuterie = null;

const utilisateurId = () => store?.state.user?.id ?? null;

/** Ventes en attente de l'utilisateur connecté (seul lui peut les envoyer en son nom) */
export function mesVentesEnAttente() {
  const id = utilisateurId();
  return horsLigne.enAttente.filter(v => v.utilisateurId === id);
}

/** Plus vieille vente non envoyée de cet utilisateur, en heures */
export function retardHeures() {
  const liste = mesVentesEnAttente();
  if (!liste.length) return 0;
  const plusAncienne = Math.min(...liste.map(v => new Date(v.le).getTime()));
  return (Date.now() - plusAncienne) / 3600000;
}

export function caisseBloquee() { return retardHeures() >= DELAI_MAX_HEURES; }

/** Erreur réseau (pas de réponse du serveur) */
export function estCoupure(e) { return !!e && !e.response && (e.horsLigne || e.code === "ERR_NETWORK" || e.code === "ECONNABORTED" || e.message === "Network Error"); }

export function cleUnique() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "hl-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

async function codeAppareil() {
  let code = await lire("appareil");
  if (!code) {
    code = Math.random().toString(36).slice(2, 6).toUpperCase();
    await ecrire("appareil", code);
  }
  return code;
}

/** Numéro provisoire imprimé sur le ticket : HL-<appareil>-<n° d'ordre> */
export async function numeroProvisoire() {
  const code = await codeAppareil();
  const n = (await lire("compteur-hl", 0)) + 1;
  await ecrire("compteur-hl", n);
  return `HL-${code}-${String(n).padStart(4, "0")}`;
}

async function sauver() {
  await ecrire("ventes-en-attente", horsLigne.enAttente);
  await ecrire("ventes-rejetees", horsLigne.rejetees);
}

// ---------- Catalogue ----------

export async function catalogue(siteId) {
  return (await lire(`catalogue-${siteId}`, null)) || { produits: [] };
}

export async function rafraichirCatalogue(siteId) {
  if (!siteId) return;
  try {
    const { data } = await api.get("produits/catalogue-caisse", { site: siteId });
    await ecrire(`catalogue-${siteId}`, data);
    horsLigne.catalogueLe = data.le;
  } catch (e) { /* on garde la copie précédente */ }
}

/** Retire du stock gardé sur l'appareil ce qui vient d'être vendu hors ligne */
export async function decompterStock(siteId, lignes) {
  const cat = await catalogue(siteId);
  lignes.forEach(l => {
    const p = cat.produits.find(x => x.id === l.produitId);
    if (p && p.stock !== null) p.stock = Math.round((p.stock - l.quantiteBase) * 1000) / 1000;
  });
  await ecrire(`catalogue-${siteId}`, cat);
}

/** Recherche dans le catalogue gardé : nom, référence ou code-barres (y compris d'un carton) */
export async function chercherLocal(siteId, q) {
  const texte = String(q || "").trim().toLowerCase();
  if (!texte) return { data: [], exact: false };
  const cat = await catalogue(siteId);
  for (const p of cat.produits) {
    if (p.codeBarres && p.codeBarres.toLowerCase() === texte) return { data: [{ ...p, uniteScannee: p.uniteBase }], exact: true };
    const u = (p.unites || []).find(x => x.codeBarres && x.codeBarres.toLowerCase() === texte);
    if (u) return { data: [{ ...p, uniteScannee: u.code }], exact: true };
  }
  const data = cat.produits
    .filter(p => p.nom.toLowerCase().includes(texte) || (p.reference || "").toLowerCase().includes(texte) || (p.codeBarres || "").includes(texte))
    .slice(0, 25).map(p => ({ ...p, uniteScannee: p.uniteBase }));
  return { data, exact: false };
}

// ---------- File d'attente ----------

export async function mettreEnFile(vente) {
  horsLigne.enAttente.push({ ...vente, utilisateurId: utilisateurId(), essais: 0 });
  await sauver();
}

/** Envoie les ventes en attente de l'utilisateur connecté, une par une, dans l'ordre */
export async function synchroniser() {
  if (horsLigne.envoiEnCours || !store?.state.token) return;
  const aEnvoyer = mesVentesEnAttente();
  if (!aEnvoyer.length) return;
  horsLigne.envoiEnCours = true;
  let envoyees = 0;
  try {
    for (const v of aEnvoyer) {
      try {
        await api.client.post("/ventes", { ...v.corps, horsLigne: { numero: v.numero, le: v.le } }, {
          headers: { "Idempotency-Key": v.cle, "X-Hors-Ligne-Le": v.le },
          silencieux: true,
        });
        horsLigne.enAttente = horsLigne.enAttente.filter(x => x.cle !== v.cle);
        envoyees++;
      } catch (e) {
        if (estCoupure(e)) { horsLigne.joignable = false; break; }
        const statut = e.response?.status;
        if (statut === 401 || statut === 402 || statut === 409 || statut >= 500) break; // à réessayer plus tard
        // Refusée (400, 403, 404…) : mise de côté avec le motif, pour le gérant
        const d = e.response?.data;
        horsLigne.enAttente = horsLigne.enAttente.filter(x => x.cle !== v.cle);
        horsLigne.rejetees.push({ ...v, motif: d?.message || d?.detail || d?.["hydra:description"] || `Refus du serveur (${statut})`, refuseeLe: new Date().toISOString() });
      }
      await sauver();
    }
  } finally {
    horsLigne.envoiEnCours = false;
    await sauver();
  }
  if (envoyees) {
    window.dispatchEvent(new CustomEvent("ventes-hors-ligne-envoyees", { detail: { nombre: envoyees } }));
    store?.dispatch("chargerCaisse");
  }
}

/** Remet une vente refusée dans la file (après correction côté serveur, ex. caisse rouverte) */
export async function reessayer(cle) {
  const v = horsLigne.rejetees.find(x => x.cle === cle);
  if (!v) return;
  horsLigne.rejetees = horsLigne.rejetees.filter(x => x.cle !== cle);
  const { motif, refuseeLe, ...reste } = v; // eslint-disable-line no-unused-vars
  horsLigne.enAttente.push(reste);
  await sauver();
  synchroniser();
}

/** Le gérant a traité la vente refusée (ressaisie, remboursement…) : on la retire de la liste */
export async function oublier(cle) {
  horsLigne.rejetees = horsLigne.rejetees.filter(x => x.cle !== cle);
  await sauver();
}

// ---------- Démarrage ----------

export async function demarrerHorsLigne(leStore) {
  store = leStore;
  horsLigne.enAttente = await lire("ventes-en-attente", []);
  horsLigne.rejetees = await lire("ventes-rejetees", []);
  horsLigne.pret = true;

  // Joignabilité réelle : chaque réponse du serveur la confirme, chaque coupure la dément
  api.client.interceptors.response.use(
    r => { if (!horsLigne.joignable) { horsLigne.joignable = true; synchroniser(); } return r; },
    e => { if (estCoupure(e)) horsLigne.joignable = false; else if (e.response) horsLigne.joignable = true; return Promise.reject(e); }
  );
  window.addEventListener("online", () => { horsLigne.joignable = true; synchroniser(); });
  window.addEventListener("offline", () => { horsLigne.joignable = false; });
  clearInterval(minuterie);
  minuterie = setInterval(async () => {
    if (!mesVentesEnAttente().length || horsLigne.envoiEnCours) return;
    // Petit appel pour savoir si le serveur répond de nouveau, puis envoi
    try { await api.get("caisse/ma-session"); horsLigne.joignable = true; } catch (e) { /* toujours coupé */ }
    if (horsLigne.joignable) synchroniser();
  }, INTERVALLE_MS);
  synchroniser();
}
